import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../lib/api';

export interface PendingApplicationRow {
  id: string;
  legalName: string;
  status: 'SUBMITTED' | 'NEEDS_INFO';
  submittedAt: string | null;
  isPrepay: boolean;
  creditLine: string | null;
  city: string | null;
  state: string | null;
  region: string;
  salesRepUserId: string | null;
}

// The admin detail response (server adminDetailView). Sensitive values are
// masked to last4 here; the raw SSN/bank come from the audited reveal endpoint.
type Nullable = string | null;

export interface ApplicationDetailPrincipal {
  id: string;
  fullName: Nullable;
  title: Nullable;
  percentOwned: number | null;
  home: string;
  ssnLast4: Nullable;
}
export interface ApplicationDetailTradeRef {
  id?: string;
  companyName: Nullable;
  contactName: Nullable;
  phone: Nullable;
  email: Nullable;
  line1: Nullable;
  city: Nullable;
  state: Nullable;
  zip: Nullable;
}
export interface ApplicationDetailLocation {
  id?: string;
  name: Nullable;
  line1: Nullable;
  line2: Nullable;
  city: Nullable;
  state: Nullable;
  zip: Nullable;
  deliveryNotes: Nullable;
  isPrimary?: boolean;
  isHeadquarters?: boolean;
}
export interface ApplicationDetailTeamMember {
  id?: string;
  name: string;
  email: string;
  role: string;
  locationAccess: number[];
}
export interface ApplicationDetail {
  id: string;
  status: string;
  submittedAt: Nullable;
  region: string;
  legalName: string;
  dba: Nullable;
  businessType: Nullable;
  entityType: Nullable;
  yearIncorporated: number | null;
  stateOfIncorporation: Nullable;
  ein: Nullable;
  office: {
    line1: Nullable;
    line2: Nullable;
    city: Nullable;
    state: Nullable;
    zip: Nullable;
    phone: Nullable;
    fax: Nullable;
    website: Nullable;
  };
  owner: {
    firstName: Nullable;
    lastName: Nullable;
    title: Nullable;
    email: Nullable;
    mobile: Nullable;
    heardAboutUs: Nullable;
    heardAboutUsDetail: Nullable;
    emailVerified: boolean;
  };
  billing: { contact: Nullable; email: Nullable; phone: Nullable };
  creditLine: Nullable;
  isPrepay: boolean;
  annualTonsRange: Nullable;
  hasBankruptcy: boolean | null;
  bankruptcyExplanation: Nullable;
  bank: null | {
    name: Nullable;
    phone: Nullable;
    address: string;
    contact: Nullable;
    accountType: Nullable;
  };
  principals: ApplicationDetailPrincipal[];
  tradeReferences: ApplicationDetailTradeRef[];
  tradeReferenceFileUrl: Nullable;
  locations: ApplicationDetailLocation[];
  teamMembers: ApplicationDetailTeamMember[];
  products: { id: string; name: string }[];
  taxExempt: boolean;
  resale: null | {
    state: Nullable;
    sellerType: Nullable;
    inBusinessOf: Nullable;
    registrationNumber: Nullable;
    description: Nullable;
  };
  signed: {
    termsVersion: Nullable;
    agreedToTerms: boolean;
    agreedPersonalGuarantee: boolean;
    signer: Nullable;
    signerTitle: Nullable;
    signedAt: Nullable;
    signedIp: Nullable;
  };
  customerStatus: Nullable;
  customerId: Nullable;
  salesRepUserId: Nullable;
  decision: {
    tierId: Nullable;
    creditLimitCents: number | null;
    note: Nullable;
    needsInfoMessage: Nullable;
    deniedReason: Nullable;
    reviewedAt: Nullable;
  };
}

const qk = {
  pending: ['applications', 'admin', 'pending'] as const,
  detail: (id: string) => ['applications', 'admin', id] as const,
};

export function usePendingApplications() {
  return useQuery({
    queryKey: qk.pending,
    queryFn: async () =>
      (await api.get('/applications/admin/pending')).data as {
        applications: PendingApplicationRow[];
      },
  });
}

export function useApplicationDetail(id: string | null) {
  return useQuery({
    queryKey: id ? qk.detail(id) : ['applications', 'admin', 'none'],
    enabled: Boolean(id),
    queryFn: async () =>
      (await api.get(`/applications/admin/${id}`)).data as { application: ApplicationDetail },
  });
}

export function useRevealField(id: string) {
  return useMutation<{ value: string }, any, { kind: 'ssn'; principalId?: string }>({
    mutationFn: async (body) => (await api.post(`/applications/admin/${id}/reveal`, body)).data,
  });
}

function invalidate(qc: ReturnType<typeof useQueryClient>, id: string) {
  qc.invalidateQueries({ queryKey: qk.pending });
  qc.invalidateQueries({ queryKey: qk.detail(id) });
  qc.invalidateQueries({ queryKey: ['users', 'me'] });
  qc.invalidateQueries({ queryKey: ['authUser'] });
}

export function useApproveApplication(id: string) {
  const qc = useQueryClient();
  return useMutation<
    { status: string; customerId: string; creditLimitPush?: string | null },
    any,
    {
      tierId?: string;
      creditLimitCents?: number | null;
      salesRepUserId?: string;
      productIds?: string[];
      note?: string;
    }
  >({
    mutationFn: async (body) => (await api.post(`/applications/admin/${id}/approve`, body)).data,
    onSuccess: () => invalidate(qc, id),
  });
}

export function useRequestInfo(id: string) {
  const qc = useQueryClient();
  return useMutation<{ status: string }, any, string>({
    mutationFn: async (message) =>
      (await api.post(`/applications/admin/${id}/request-info`, { message })).data,
    onSuccess: () => invalidate(qc, id),
  });
}

export function useDenyApplication(id: string) {
  const qc = useQueryClient();
  return useMutation<{ status: string }, any, string>({
    mutationFn: async (reason) =>
      (await api.post(`/applications/admin/${id}/deny`, { reason })).data,
    onSuccess: () => invalidate(qc, id),
  });
}

export interface TierOption {
  id: string;
  name: string;
  discountAmountCents: number;
  badgeColor: string;
}

export function useOrgTiers() {
  return useQuery({
    queryKey: ['organization', 'customer-tiers'],
    queryFn: async () =>
      ((await api.get('/organization/customer-tiers')).data as { tiers: TierOption[] }).tiers,
  });
}

export interface RepOption {
  uid: string;
  name: string;
  email: string;
}

export function useOrgReps() {
  return useQuery({
    queryKey: ['organization', 'members', 'reps'],
    queryFn: async () => {
      const data = (await api.get('/organization/members')).data as any;
      const members: any[] = data?.members ?? data ?? [];
      return members
        .filter((m) => m.user?.firebaseUid)
        .map((m) => ({
          uid: m.user.firebaseUid,
          name: m.user.name || m.user.email || 'Member',
          email: m.user.email || '',
        })) as RepOption[];
    },
  });
}

// Open a generated application PDF in a new tab (credit app or resale cert).
export async function openApplicationPdf(id: string, kind: 'pdf' | 'resale-pdf' = 'pdf') {
  const res = await api.get(`/applications/admin/${id}/${kind}`, {
    params: { disposition: 'inline' },
    responseType: 'blob',
  });
  const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
  window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}
